import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaRuler, FaEdit, FaTrash, FaSave, FaTimes, FaCopy } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useSavedMeasurements } from '../context/SavedMeasurementsContext';
import SEOHead from '../components/SEOHead';
import './SavedMeasurements.css';

const SavedMeasurements = () => {
  const { currentUser } = useAuth();
  const { 
    savedMeasurements, 
    loading, 
    error, 
    deleteMeasurementSet, 
    updateMeasurementSet 
  } = useSavedMeasurements();
  
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editMeasurements, setEditMeasurements] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Filter measurements based on type and search
  const filteredMeasurements = savedMeasurements.filter(measurement => {
    const matchesType = filterType === 'all' || measurement.productType.toLowerCase() === filterType.toLowerCase();
    const matchesSearch = measurement.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  });

  // Get unique product types for filter
  const productTypes = [...new Set(savedMeasurements.map(m => m.productType))];

  const handleEdit = (measurement) => {
    setEditingId(measurement.id);
    setEditName(measurement.name);
    setEditMeasurements({ ...measurement.measurements });
  };

  const handleSaveEdit = async () => {
    if (!editName.trim()) {
      return;
    }
    
    try {
      await updateMeasurementSet(editingId, { 
        name: editName.trim(),
        measurements: editMeasurements
      });
      setEditingId(null);
      setEditName('');
      setEditMeasurements({});
    } catch (error) {
      console.error('Error updating measurement:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditMeasurements({});
  };

  const handleMeasurementChange = (key, value) => {
    setEditMeasurements(prev => ({
      ...prev,
      [key]: parseFloat(value) || 0
    }));
  };

  const handleDelete = async (measurementId) => {
    try {
      await deleteMeasurementSet(measurementId);
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting measurement:', error);
    }
  };

  const formatMeasurementValue = (key, value) => {
    // Format the measurement key for better display
    const formattedKey = key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .replace(/_/g, ' ')
      .trim();
    
    return `${formattedKey}: ${value}"`;
  };

  const copyMeasurements = (measurements) => {
    const text = Object.entries(measurements)
      .map(([key, value]) => formatMeasurementValue(key, value))
      .join('\n');
    
    navigator.clipboard.writeText(text).then(() => {
      // You could add a toast notification here
    });
  };

  // Helper function to find measurement value by common patterns
  const findMeasurementValue = (measurements, patterns) => {
    // Debug: log available measurement keys
    
    for (const pattern of patterns) {
      if (measurements[pattern] !== undefined && measurements[pattern] !== null) {
        return measurements[pattern];
      }
    }
    return 'N/A';
  };

  if (!currentUser) {
    return (
      <div className="saved-measurements-page">
        <SEOHead 
          title="Saved Measurements"
          description="Manage your saved custom measurements for future orders at Vastré By Vijh"
        />
        <div className="auth-required">
          <div className="auth-required-content">
            <FaRuler className="auth-icon" />
            <h2>Sign In Required</h2>
            <p>Please sign in to view and manage your saved measurements.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="saved-measurements-page">
      <SEOHead 
        title="Saved Measurements"
        description="Manage your saved custom measurements for future orders at Vastré By Vijh"
      />
      
      {/* Compact Header */}
      <div className="saved-measurements-header">
        <div className="container">
          <h1>Saved Measurements</h1>
          <p>Manage your custom measurements</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="saved-measurements-content">
        <div className="container">
          {/* Compact Controls */}
          <div className="measurements-controls-compact">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-compact"
            />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="filter-compact"
            >
              <option value="all">All</option>
              {productTypes.map(type => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading your measurements...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="error-state">
              <p>Error: {error}</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && filteredMeasurements.length === 0 && (
            <div className="empty-state">
              <FaRuler className="empty-icon" />
              <h3>No Saved Measurements</h3>
              <p>
                {searchTerm || filterType !== 'all' 
                  ? 'No measurements match your current filters.'
                  : 'You haven\'t saved any measurements yet. Start by adding custom measurements to any product.'
                }
              </p>
            </div>
          )}

          {/* Ultra Compact Measurements List */}
          <AnimatePresence>
            <div className="measurements-list-compact">
              {filteredMeasurements.map((measurement) => (
                <motion.div
                  key={measurement.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2 }}
                  className="measurement-item-compact"
                >
                  {editingId === measurement.id ? (
                    <div className="edit-mode-compact">
                      <div className="edit-header">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="edit-name-input-compact"
                          placeholder="Measurement name"
                        />
                        <div className="edit-actions-compact">
                          <button onClick={handleSaveEdit} className="save-btn-compact" disabled={!editName.trim()}>
                            <FaSave />
                          </button>
                          <button onClick={handleCancelEdit} className="cancel-btn-compact">
                            <FaTimes />
                          </button>
                        </div>
                      </div>
                      <div className="edit-measurements-grid">
                        {Object.entries(editMeasurements).map(([key, value]) => (
                          <div key={key} className="edit-measurement-item">
                            <label className="edit-label">
                              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).replace(/_/g, ' ').trim()}
                            </label>
                            <input
                              type="number"
                              value={value}
                              onChange={(e) => handleMeasurementChange(key, e.target.value)}
                              className="edit-value-input"
                              step="0.1"
                              min="0"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="view-mode-compact">
                      <div className="item-header">
                        <div className="item-info">
                          <h3 className="item-name">{measurement.name}</h3>
                          <span className="item-type">{measurement.productType}</span>
                        </div>
                        <div className="measurement-item-actions">
                          <button onClick={() => handleEdit(measurement)} className="measurement-action-btn measurement-edit-btn">
                            <FaEdit />
                            <span>Edit</span>
                          </button>
                          <button onClick={() => setShowDeleteConfirm(measurement.id)} className="measurement-action-btn measurement-delete-btn">
                            <FaTrash />
                            <span>Delete</span>
                          </button>
                        </div>
                      </div>
                      <div className="measurements-summary">
                        <div className="measurement-count">
                          {Object.keys(measurement.measurements).length} measurements saved
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </AnimatePresence>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay"
            onClick={() => setShowDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="delete-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Delete Measurement</h3>
              <p>Are you sure you want to delete this measurement set? This action cannot be undone.</p>
              <div className="delete-modal-actions">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="cancel-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(showDeleteConfirm)}
                  className="delete-btn"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SavedMeasurements;
